import type { QueryClient } from "@tanstack/react-query";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils/api/api-client";
import { resolveEffectiveTenantNumber } from "@/lib/utils/env/effective-tenant";
import { ClassesApi, type GetClassResponse } from "@/openapi/generated";

export const CLASSES_BULK_MAX_IDS = 100;

export function uniqueSortedClassIds(classIds: number[]): number[] {
  return [
    ...new Set(classIds.filter((id) => Number.isFinite(id) && id > 0)),
  ].sort((a, b) => a - b);
}

export function classesByIdsQueryKey(classIds: number[]) {
  return [
    QueryKeys.Class,
    "bulk",
    uniqueSortedClassIds(classIds).join(","),
  ] as const;
}

function chunkIds(ids: number[], size: number): number[][] {
  const chunks: number[][] = [];
  for (let i = 0; i < ids.length; i += size) {
    chunks.push(ids.slice(i, i + size));
  }
  return chunks;
}

export async function fetchClassesByIdsBulk(
  accessToken: string,
  classIds: number[],
): Promise<GetClassResponse[]> {
  const uniqueIds = uniqueSortedClassIds(classIds);
  if (uniqueIds.length === 0) return [];

  const service = createAuthenticatedApi(ClassesApi, accessToken);
  const tenantId = resolveEffectiveTenantNumber();
  const items: GetClassResponse[] = [];

  for (const chunk of chunkIds(uniqueIds, CLASSES_BULK_MAX_IDS)) {
    const response = await service.apiClassesBulkPost({
      getBulkClassesRequest: { classIds: chunk },
      xTenantID: tenantId,
    });
    items.push(...(response.items ?? []));
  }

  return items;
}

export function seedClassQueries(
  queryClient: QueryClient,
  classes: GetClassResponse[],
) {
  for (const classItem of classes) {
    const classId = Number(classItem.classId);
    if (!Number.isFinite(classId) || classId <= 0) continue;
    queryClient.setQueryData([QueryKeys.Class, classId], classItem);
  }
}

export async function fetchAndSeedClassesByIds(
  queryClient: QueryClient,
  accessToken: string,
  classIds: number[],
): Promise<GetClassResponse[]> {
  const items = await fetchClassesByIdsBulk(accessToken, classIds);
  seedClassQueries(queryClient, items);
  return items;
}
